import React, { useState, useEffect, useRef } from 'react';
import { Eye } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// How long the page must stay visible before we count a view even without
// any interaction (covers real users who read without touching the mouse).
const ENGAGEMENT_DWELL_MS = 4000;

// Any of these firing once is enough to prove a human is driving the page —
// headless/scanner bots that just render the DOM and leave don't generate them.
const ENGAGEMENT_EVENTS = ['scroll', 'mousemove', 'touchstart', 'keydown'];

const ViewCounter = ({ position = 'bottom-right' }) => {
  const [views, setViews] = useState(null);
  const { isDark } = useTheme();
  const trackedRef = useRef(false);

  // Always same-origin — unlike the contact form, this doesn't need
  // REACT_APP_BACKEND_URL flexibility.
  const API_URL = "";

  // Fetch the current view count for display. Read-only — safe to call
  // immediately on mount regardless of whether this visit gets tracked.
  const fetchViewCount = async () => {
    try {
      const fetchResponse = await fetch(`${API_URL}/api/views`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (fetchResponse.ok) {
        const fetchData = await fetchResponse.json();
        const viewCount = Number(fetchData?.view_count ?? 0);
        if (viewCount > 0) {
          setViews(viewCount);
        }
      }
    } catch (error) {
      console.error('Failed to fetch view count:', error);
    }
  };

  // Record this visit as a view, then refresh the displayed count. Gated
  // behind real engagement (see effect below) so page-render-only bots
  // (link scanners, headless crawlers) never reach this.
  const trackView = async () => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';

      const trackResponse = await fetch(`${API_URL}/api/views`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone }),
      });

      if (trackResponse.ok) {
        await fetchViewCount();
      }
    } catch (error) {
      console.error('Failed to track view:', error);
    }
  };

  useEffect(() => {
    fetchViewCount();
  }, []);

  useEffect(() => {
    let dwellTimer;

    const handleEngagement = () => {
      trackView();
      cleanup();
    };

    const cleanup = () => {
      clearTimeout(dwellTimer);
      ENGAGEMENT_EVENTS.forEach(evt =>
        window.removeEventListener(evt, handleEngagement)
      );
    };

    ENGAGEMENT_EVENTS.forEach(evt =>
      window.addEventListener(evt, handleEngagement, { passive: true, once: true })
    );
    dwellTimer = setTimeout(handleEngagement, ENGAGEMENT_DWELL_MS);

    return cleanup;
  }, []);

  // Position styles
  const positionClasses = {
    'top-right': 'fixed top-20 right-6 z-50',
    'bottom-right': 'fixed bottom-3 right-3 z-30',
  };

  // Custom bottom position to align with FloatingNav (35px bottom margin)
  const customStyle = position === 'bottom-right' ? { bottom: '16px' } : {};


  // Don't render until views are fetched and count > 0
  if (views === null || views === 0) {
    return null;
  }

  return (
    <div className={positionClasses[position]} style={customStyle}>
      <div
        className={`
          flex items-center space-x-2 px-4 py-2.5 rounded-full
          ${isDark
            ? 'bg-white/10 backdrop-blur-md border border-white/20'
            : 'bg-black/5 backdrop-blur-md border border-black/10'
          }
          shadow-lg transition-all duration-300 hover:scale-105
        `}
      >
        <Eye
          className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
        />
        <span
          className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}
        >
          {views.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default ViewCounter;
